import { useEffect, useState } from 'react'
import { usePageContext } from '../../../context/page.context'

// The one place Insights colours come from (owner ruling 10). Every value
// is read from the admin's --sw-* tokens on the nearest .sw-workspace, plus
// the page font, at render time; the chart palette and the heat ramps are
// computed from them (the mockup's palette() and ramp(), ported). Panels
// read the theme again when the admin's theme switches
// (usePageContext().theme.isDark), so light and dark both follow it.
//
// The fallbacks are the same tokens' values (sso-workspace.scss), used
// only where nothing is computed (tests, a detached node).

const TOKENS = ['primary', 'ok', 'warn', 'bad', 'map', 'muted', 'line', 'line-strong', 'ink', 'surface', 'sunk']
const FALLBACK = {
  light: { primary: '#1E4B77', ok: '#1F7A4D', warn: '#9A6212', bad: '#B3261E', map: '#0F6E6E', muted: '#5B6878', line: '#DCE3EA', 'line-strong': '#C5CFDA', ink: '#1B2430', surface: '#FFFFFF', sunk: '#F7F9FB' },
  dark: { primary: '#6FA3D6', ok: '#5FC08D', warn: '#E0A955', bad: '#F08A82', map: '#5CC6C2', muted: '#9AA7B5', line: '#27323E', 'line-strong': '#35424F', ink: '#E3E9F0', surface: '#161E27', sunk: '#121922' }
}

/* ---- colour arithmetic (mockup, ported) ---- */

// [r, g, b, a] from #rgb, #rrggbb, #rrggbbaa, rgb() or rgba(); null if unreadable.
const parse = c => {
  const s = String(c || '').trim()
  let m = s.match(/^#([0-9a-f]{3,8})$/i)
  if (m) {
    let h = m[1]
    if (h.length === 3 || h.length === 4) h = [...h].map(x => x + x).join('')
    if (h.length !== 6 && h.length !== 8) return null
    const v = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16))
    return [...v, h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1]
  }
  m = s.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?\s*\)$/i)
  if (m) {
    const a = m[4] === undefined ? 1 : m[4].endsWith('%') ? parseFloat(m[4]) / 100 : parseFloat(m[4])
    return [+m[1], +m[2], +m[3], a]
  }
  return null
}
const clamp = v => Math.max(0, Math.min(255, Math.round(v)))
const hex = a => '#' + a.slice(0, 3).map(v => clamp(v).toString(16).padStart(2, '0')).join('')
const rgb = c => (parse(c) || [0, 0, 0, 1]).slice(0, 3)
export const mix = (a, b, t) => {
  const A = rgb(a)
  const B = rgb(b)
  return hex(A.map((v, i) => v + (B[i] - v) * t))
}
export const alpha = (c, a) => `rgba(${rgb(c).join(',')},${a})`
export const lum = c => {
  const [r, g, b] = rgb(c)
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
}
const toHsl = c => {
  const [r, g, b] = rgb(c).map(v => v / 255)
  const mx = Math.max(r, g, b)
  const mn = Math.min(r, g, b)
  const l = (mx + mn) / 2
  let h = 0
  let s = 0
  if (mx !== mn) {
    const d = mx - mn
    s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn)
    h = 60 * (mx === r ? (g - b) / d + (g < b ? 6 : 0) : mx === g ? (b - r) / d + 2 : (r - g) / d + 4)
  }
  return [h, s, l]
}
const fromHsl = (h, s, l) => {
  h = ((h % 360) + 360) % 360
  const a = s * Math.min(l, 1 - l)
  const f = n => {
    const k = (n + h / 30) % 12
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))
  }
  return hex([f(0), f(8), f(4)].map(v => v * 255))
}
// OKLab, for heat ramps whose steps look evenly spaced.
const toLab = c => {
  const [r, g, b] = rgb(c).map(v => {
    v /= 255
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  })
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s]
}
const fromLab = ([L, a, b]) => {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3
  return hex([4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s, -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s, -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s]
    .map(v => {
      v = Math.max(0, Math.min(1, v))
      return 255 * (v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055)
    }))
}
export const ramp = (stops, n) => {
  const labs = stops.map(toLab)
  return [...Array(n)].map((_, i) => {
    const t = (i / (n - 1)) * (labs.length - 1)
    const k = Math.min(Math.floor(t), labs.length - 2)
    const f = t - k
    return fromLab(labs[k].map((v, j) => v + (labs[k + 1][j] - v) * f))
  })
}

// The chart palette: the hue of each token, re-lit at one saturation and
// lightness per theme so categories read as one family (brighter on dark,
// deeper on light). The admin has no violet or pink token, so those two
// hues are turned from primary and bad, as the mockup's sit beside them.
const palette = T => {
  const [S, L] = T.dark ? [0.64, 0.69] : [0.6, 0.54]
  const v = (c, dh = 0, ds = 0, dl = 0) => fromHsl(toHsl(c)[0] + dh, S + ds, L + dl)
  return {
    blue: v(T.primary),
    teal: v(T.map, 0, -0.06, -0.02),
    violet: v(T.primary, 43, -0.04),
    amber: v(T.warn, 0, 0.12, 0.04),
    green: v(T.ok, 0, -0.1, -0.02),
    rose: v(T.bad, 2, 0, 0.02),
    pink: v(T.bad, -37, -0.04, 0.02),
    sky: v(T.primary, -14, 0.06, 0.08),
    indigo: v(T.primary, 21, -0.08, -0.02),
    slate: fromHsl(toHsl(T.muted)[0], 0.18, 0.6)
  }
}
// The order colours are handed out in (apps, record types, people).
export const SERIES = ['blue', 'teal', 'violet', 'amber', 'green', 'rose', 'pink', 'sky', 'indigo', 'slate']

// An opaque colour: a translucent token (Bootstrap's muted text is rgba)
// is laid over the surface, so the arithmetic above sees what is drawn.
const solid = (c, surface) => {
  const p = parse(c)
  if (!p) return null
  if (p[3] >= 1) return hex(p)
  const s = rgb(surface)
  return hex(p.slice(0, 3).map((v, i) => s[i] + (v - s[i]) * p[3]))
}

// The theme: the tokens (as opaque hex), the font, dark or not, and the
// palette. el: any node inside the page; isDark picks the fallbacks.
export const readTheme = (el, isDark = false) => {
  const fb = FALLBACK[isDark ? 'dark' : 'light']
  let host = null
  try {
    host = (el && el.closest && el.closest('.sw-workspace')) || document.querySelector('.sw-workspace')
  } catch (e) {
    host = null
  }
  const style = host ? window.getComputedStyle(host) : null
  const raw = n => (style ? style.getPropertyValue(`--sw-${n}`).trim() : '')
  const surface = solid(raw('surface'), fb.surface) || fb.surface
  const T = {}
  TOKENS.forEach(n => {
    T[n === 'line-strong' ? 'lineStrong' : n] = n === 'surface' ? surface : solid(raw(n), surface) || fb[n]
  })
  T.faint = mix(T.muted, T.surface, 0.35)
  let font = ''
  try {
    font = window.getComputedStyle(document.body).fontFamily
  } catch (e) {
    font = ''
  }
  T.sans = font || 'sans-serif'
  T.dark = lum(T.surface) < 0.4
  T.P = palette(T)
  return T
}

const reducedMotion = () => {
  try {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  } catch (e) {
    return false
  }
}

// What every option starts from: the font, ink, the tooltip in surface /
// ink / line-strong, and no animation for a reader who asked for less motion.
export const baseOption = T => ({
  animation: !reducedMotion(),
  textStyle: { fontFamily: T.sans, color: T.ink },
  tooltip: {
    confine: true,
    backgroundColor: T.surface,
    borderColor: T.lineStrong,
    borderWidth: 1,
    padding: [6, 10],
    textStyle: { color: T.ink, fontSize: 12, fontFamily: T.sans },
    extraCssText: `box-shadow: 0 8px 24px ${alpha(T.ink, 0.18)}; border-radius: 8px;`
  }
})
export const tooltip = (T, formatter) => ({ ...baseOption(T).tooltip, trigger: 'item', formatter })
export const dotHtml = c => `<i style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${c};margin-right:6px"></i>`
// Text on a filled shape: the surface colour on mid or deep fills, ink on light ones.
export const onFill = (c, T) => (Math.abs(lum(c) - lum(T.surface)) > Math.abs(lum(c) - lum(T.ink)) ? T.surface : T.ink)
// A left-to-right gradient, as a plain ECharts colour object.
export const hGradient = (from, to) => ({ type: 'linear', x: 0, y: 0, x2: 1, y2: 0, colorStops: [{ offset: 0, color: from }, { offset: 1, color: to }] })
export const vGradient = (from, to) => ({ type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: from }, { offset: 1, color: to }] })

// A colour that stays with a name across panels (an app is the same colour
// in the mix, the flows and the sessions).
// Two names can hash to one colour, so a chart whose marks must each be
// told apart (the network's record types) uses kindColors below instead.
const hashOf = s => [...String(s)].reduce((h, c) => Math.imul(h ^ c.charCodeAt(0), 16777619), 2166136261) >>> 0
export const colorFor = (T, name) => T.P[SERIES[hashOf(name) % SERIES.length]]

// Identity colours for a chart whose marks all sit side by side, where any
// two may touch (the network: every pair is a neighbour, not just the next
// one in a legend). People keep one reserved colour, the primary's hue; the
// kinds of record get three more hues, turned from ok, warn and bad (the
// pink beside bad), and nothing else: the dataviz validator, over all
// pairs and in both themes, passes four colours at these OKLCH steps
// (with the admin's tokens: worst colour-blind ΔE 11.7 light / 11.3 dark,
// worst normal-vision ΔE 22.2 / 19.9) and no set of five clears it. Two
// light-theme fills sit under 3:1 on the surface, so the chart names its
// busiest nodes and keeps its table ("Show the numbers"). A kind past the third is "Other", in
// a neutral grey, never a reused colour. [hue offset, L light, L dark, C].
const IDENTITY = {
  people: ['primary', 0, 0.55, 0.62, 0.15],
  kinds: [['bad', -42, 0.5, 0.56, 0.17], ['ok', 0, 0.72, 0.66, 0.16], ['warn', 3, 0.62, 0.52, 0.13]]
}
const okHue = c => {
  const [, a, b] = toLab(c)
  return (Math.atan2(b, a) * 180) / Math.PI
}
const fromLch = (L, C, h) => fromLab([L, C * Math.cos((h * Math.PI) / 180), C * Math.sin((h * Math.PI) / 180)])
const identity = (T, [token, dh, Ll, Ld, C]) => fromLch(T.dark ? Ld : Ll, T.dark ? C * 0.92 : C, okHue(T[token]) + dh)
export const peopleColor = T => identity(T, IDENTITY.people)
export const KIND_COLORS = IDENTITY.kinds.length
export const otherColor = T => mix(T.muted, T.surface, T.dark ? 0.25 : 0.2)
// kinds: the kinds in order of weight, heaviest first. Returns a Map of
// kind -> { color, other }: the first KIND_COLORS kinds get their own
// colour, the rest are other.
export const kindColors = (T, kinds) => new Map(kinds.map((k, i) => [k, i < IDENTITY.kinds.length ? { color: identity(T, IDENTITY.kinds[i]), other: false } : { color: otherColor(T), other: true }]))

// The heat ramp: a neutral for zero, then six even OKLab steps from a
// clear tint of the hue to its deepest shade. Zero is barely off the
// surface (the grid still reads, owner 2026-10-05) and the first step is a
// ~40% tint, so one or two entries never sink into the grey; the busiest
// cells stay the darkest. Changes use the primary hue, course launches the
// pink, and "all" the violet between them.
const HEAT_HUE = { changes: 'blue', launches: 'pink', all: 'violet' }
export const HEAT_EMPTY = { light: 0.4, dark: 0.55 }
// the first step's tint of the hue: deeper on dark, where a 40% tint of a
// light hue still sits close to the surface
export const HEAT_FIRST = { light: 0.4, dark: 0.5 }
export const heatColors = (T, kind = 'all') => {
  const hue = HEAT_HUE[kind] || HEAT_HUE.all
  const c = T.P[hue]
  const deep = kind === 'changes' ? T.primary : mix(c, T.ink, 0.35)
  const stops = T.dark ? [mix(T.surface, c, HEAT_FIRST.dark), c, mix(c, T.ink, 0.55)] : [mix(T.surface, c, HEAT_FIRST.light), c, deep]
  return [mix(T.surface, T.line, T.dark ? HEAT_EMPTY.dark : HEAT_EMPTY.light), ...ramp(stops, 6)]
}

// Whether the page is dark right now: the provider's data-bs-theme on <body>
// (set by page.context after its children render), else the context's flag.
const bodyIsDark = fallback => {
  try {
    const attr = document.body && document.body.getAttribute('data-bs-theme')
    return attr ? attr === 'dark' : fallback
  } catch (e) {
    return fallback
  }
}

// The theme for a panel: read once on mount, and again the moment the
// admin's theme switch reaches the page. The provider sets data-bs-theme
// after its children render, so a MutationObserver on that attribute
// re-reads the tokens as soon as it changes, before the next paint, and the
// charts get their new option at once. (A re-read on the next animation
// frame alone left the graph's labels in the old theme's ink until
// something redrew it: dev click-through 2026-10-04.) The context flag
// still triggers a read, for a switch that lands before the observer does.
// ref: the panel's own node.
export const useChartTheme = ref => {
  const page = usePageContext()
  const isDark = Boolean(page && page.theme && page.theme.isDark)
  const [T, setT] = useState(() => readTheme(ref && ref.current, bodyIsDark(isDark)))
  useEffect(() => {
    const read = () => {
      const next = readTheme(ref && ref.current, bodyIsDark(isDark))
      setT(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next))
    }
    read()
    const later = typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame : fn => setTimeout(fn, 0)
    const cancel = typeof window.cancelAnimationFrame === 'function' ? window.cancelAnimationFrame : clearTimeout
    const id = later(read)
    let mo = null
    if (typeof window.MutationObserver === 'function' && document.body) {
      mo = new window.MutationObserver(read)
      mo.observe(document.body, { attributes: true, attributeFilter: ['data-bs-theme'] })
    }
    return () => {
      cancel(id)
      if (mo) mo.disconnect()
    }
  }, [isDark, ref])
  return T
}
